import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Descriptions, Radio, Select } from 'antd';
import {
  ISpeakingRubric,
  IPostSpeakingRubricReq,
} from '../../../models/newSpeaking.model';
import Button from '../../Button';
import { useHistory } from 'react-router-dom';
import useNotification from '../../../hooks/useNotification';
import { YN } from '../../../models/common.api.model';
import useAuth from '../../../hooks/useAuth';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/rootReducer';
import SelectEduStep from '../../SelectEduStep';

interface IProps {
  data?: ISpeakingRubric;
  submit(body: IPostSpeakingRubricReq): void;
}

const SpeakingRubricEditor: FC<IProps> = ({
  data,
  submit,
}) => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const history = useHistory();
  const { infoNotification } = useNotification();
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  const TE0010 = commonCodes.find(item => item.commonCode === 'TE0010')?.detailCode;

  const [eduStepCode, setEduStepCode] = useState<string>();
  const [rubricType, setRubricType] = useState<string>();
  const [rubricName, setRubricName] = useState('');
  const [useYN, setUseYN] = useState<YN>('Y');
  const [memo, setMemo] = useState('');

  useEffect(() => {
    if (data) {
      setEduStepCode(data.eduStepCode);
      setRubricType(data.rubricType);
      setRubricName(data.rubricName);
      setUseYN(data.useYN);
      setMemo(data.memo ?? '');
    }
  }, [data]);

  const onSubmit = () => {
    if (!eduStepCode) {
      return infoNotification(t('cbt.speakingRubric.1'));
    }
    if (!rubricType) {
      return infoNotification(t('cbt.speakingRubric.2'));
    }
    if (!rubricName) {
      return infoNotification(t('cbt.speakRubric.message1'));
    }
    if (!useYN) {
      return infoNotification(t('cbt.speakRubric.message2'));
    }

    submit({
      eduStepCode,
      rubricType,
      rubricName,
      useYN,
      memo: memo === null ? undefined : memo
    });
  };

  return (
    <div className='comm-table__area rowType'>
      <Descriptions
        bordered
        size="small"
        column={1}
        labelStyle={{ width: '25%' }}
      >
        <Descriptions.Item label={t('common.label.1')}>
          <SelectEduStep
            eduStepCode={eduStepCode}
            setEduStepCode={setEduStepCode}
            placeholder={t('student.student.placeholder6')}
            width={200}
            disabled={!!data}
            noDefault
          />
        </Descriptions.Item>
        <Descriptions.Item label={t('cbt.speakingRubric.3')}>
          <div style={{ width: '200px' }}>
            <Select
              className="w-100"
              placeholder={t('student.student.placeholder6')}
              value={rubricType}
              onChange={value => setRubricType(value)}
              disabled={!!data}
            >
              {
                TE0010?.map(item => (
                  <Select.Option
                    key={item.detailCode}
                    value={item.detailCode}
                  >
                    {item.korName}
                  </Select.Option>
                ))
              }
            </Select>
          </div>
        </Descriptions.Item>
        <Descriptions.Item label={t('cbt.speakRubric.column1')}>
          <input
            type="text"
            className="form-control input-text"
            value={rubricName}
            onChange={({ target: { value } }) => setRubricName(value)}
          />
        </Descriptions.Item>
        <Descriptions.Item label={t('cbt.speakRubric.column2')}>
          <div className='auth-radio__area'>
            <Radio.Group
              onChange={({ target: { value } }) => setUseYN(value)}
              value={useYN}
            >
              <Radio value="Y">Y</Radio>
              <Radio value="N">N</Radio>
            </Radio.Group>
          </div>
        </Descriptions.Item>
        <Descriptions.Item label={t('cbt.speakRubric.column3')}>
          <textarea
            rows={3}
            value={memo ?? ''}
            onChange={({ target: { value } }) => setMemo(value)}
          />
        </Descriptions.Item>
      </Descriptions>
      <div className="comm-bx__between">
        <div className="comm-btn__area">
          <Button
            className="btn-basic gray-line"
            onClick={() => history.push('/cbt/speaking-rubric')}
            text={t('common.button.list')}
          />
        </div>
        <div className="comm-btn__area right">
          <Button
            className="btn-basic pink"
            onClick={onSubmit}
            text={t('common.button.save')}
            writeAuthYn={writeAuthYn}
          />
        </div>
      </div>
    </div>
  );
};

export default SpeakingRubricEditor;
